import React from "react";
import "./SkeletonLoader.css";

const SkeletonLoader = ({ variant = "default", count = 1, className = "" }) => {
  const renderSkeleton = () => {
    switch (variant) {
      case "patient-card":
        return (
          <div className={`skeleton-patient-card ${className}`}>
            <div className="skeleton-avatar"></div>
            <div className="skeleton-content">
              <div className="skeleton-line skeleton-title"></div>
              <div className="skeleton-line skeleton-subtitle"></div>
              <div className="skeleton-line skeleton-text"></div>
            </div>
          </div>
        );

      case "patient-list":
        return (
          <div className={`skeleton-patient-list ${className}`}>
            {Array.from({ length: count }).map((_, index) => (
              <div key={index} className="skeleton-patient-item">
                <div className="skeleton-avatar-small"></div>
                <div className="skeleton-patient-info">
                  <div className="skeleton-line skeleton-name"></div>
                  <div className="skeleton-line skeleton-details"></div>
                </div>
              </div>
            ))}
          </div>
        );

      case "table":
        return (
          <div className={`skeleton-table ${className}`}>
            <div className="skeleton-table-header">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="skeleton-line skeleton-header-cell"></div>
              ))}
            </div>
            {Array.from({ length: count }).map((_, rowIndex) => (
              <div key={rowIndex} className="skeleton-table-row">
                {Array.from({ length: 3 }).map((_, colIndex) => (
                  <div key={colIndex} className="skeleton-line skeleton-table-cell"></div>
                ))}
              </div>
            ))}
          </div>
        );

      case "admission-card":
        return (
          <div className={`skeleton-admission-card ${className}`}>
            <div className="skeleton-card-header">
              <div className="skeleton-line skeleton-card-title"></div>
              <div className="skeleton-button"></div>
            </div>
            <div className="skeleton-grid">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="skeleton-line skeleton-grid-item"></div>
              ))}
            </div>
            <div className="skeleton-line skeleton-full-row"></div>
          </div>
        );

      case "patient-info":
        return (
          <div className={`skeleton-patient-info ${className}`}>
            <div className="skeleton-header-section">
              <div className="skeleton-avatar-large"></div>
              <div className="skeleton-header-content">
                <div className="skeleton-line skeleton-main-title"></div>
                <div className="skeleton-line skeleton-id-badge"></div>
              </div>
            </div>
            <div className="skeleton-info-grid">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="skeleton-info-card">
                  <div className="skeleton-card-icon"></div>
                  <div className="skeleton-card-title"></div>
                  <div className="skeleton-card-content">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="skeleton-line skeleton-info-row"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "appointment-card":
        return (
          <div className={`skeleton-appointment-card ${className}`}>
            {Array.from({ length: count }).map((_, index) => (
              <div key={index} className="skeleton-card">
                <div className="skeleton-card-header">
                  <div className="skeleton-line skeleton-card-title"></div>
                  <div className="skeleton-badge"></div>
                </div>
                <div className="skeleton-card-body">
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line skeleton-short"></div>
                </div>
                <div className="skeleton-card-actions">
                  <div className="skeleton-button"></div>
                  <div className="skeleton-button"></div>
                </div>
              </div>
            ))}
          </div>
        );

      case "modal":
        return (
          <div className={`skeleton-modal ${className}`}>
            <div className="skeleton-modal-header">
              <div className="skeleton-line skeleton-modal-title"></div>
            </div>
            <div className="skeleton-modal-body">
              <div className="skeleton-line skeleton-input"></div>
              <div className="skeleton-line skeleton-input"></div>
              <div className="skeleton-line skeleton-textarea"></div>
              <div className="skeleton-line skeleton-textarea"></div>
            </div>
            <div className="skeleton-modal-footer">
              <div className="skeleton-button"></div>
              <div className="skeleton-button"></div>
            </div>
          </div>
        );

      case "overlay":
        return (
          <div className={`skeleton-overlay ${className}`}>
            <div className="skeleton-spinner-container">
              <div className="skeleton-spinner"></div>
            </div>
          </div>
        );

      case "discharged-list":
        return (
          <div className={`skeleton-discharged-list ${className}`}>
            {Array.from({ length: count }).map((_, index) => (
              <div key={index} className="skeleton-discharged-card">
                <div className="skeleton-card-top">
                  <div className="skeleton-line skeleton-name"></div>
                  <div className="skeleton-badge"></div>
                </div>
                <div className="skeleton-card-grid">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="skeleton-line skeleton-grid-item"></div>
                  ))}
                </div>
                <div className="skeleton-line skeleton-reason"></div>
                <div className="skeleton-button"></div>
              </div>
            ))}
          </div>
        );

      case "dashboard-summary-card":
        return (
          <div className={`skeleton-dashboard-card ${className}`}>
            <div className="skeleton-card-icon-circle"></div>
            <div className="skeleton-card-info">
              <div className="skeleton-line skeleton-label-short"></div>
              <div className="skeleton-line skeleton-value-large"></div>
              <div className="skeleton-line skeleton-sub-info"></div>
            </div>
          </div>
        );

      case "chart-placeholder":
        return (
          <div className={`skeleton-chart-box ${className}`}>
            <div className="skeleton-chart-header">
              <div className="skeleton-line skeleton-chart-title"></div>
              <div className="skeleton-line skeleton-chart-subtitle"></div>
            </div>
            <div className="skeleton-chart-visual"></div>
          </div>
        );

      case "invoice-stat-card":
        return (
          <div className={`skeleton-invoice-stat-card ${className}`}>
            <div className="skeleton-icon-box-square"></div>
            <div className="skeleton-stat-content">
              <div className="skeleton-stat-header-row">
                <div className="skeleton-line skeleton-stat-label"></div>
                <div className="skeleton-line skeleton-stat-percent"></div>
              </div>
              <div className="skeleton-line skeleton-stat-value"></div>
              <div className="skeleton-line skeleton-stat-desc"></div>
              <div className="skeleton-progress-bar-placeholder"></div>
            </div>
          </div>
        );

      default:
        return (
          <div className={`skeleton-default ${className}`}>
            <div className="skeleton-line"></div>
            <div className="skeleton-line"></div>
            <div className="skeleton-line skeleton-short"></div>
          </div>
        );
    }
  };

  return <div className="skeleton-wrapper">{renderSkeleton()}</div>;
};

export default SkeletonLoader;

